import type {
  MappedNotificationCounts,
  Notification,
  NotificationSummaryClauseModel,
  NotificationSummaryModel,
  NotificationSummaryTileModel,
} from "../types";
import {
  getNotificationCategory,
} from "../constants/config";

export {
  UPDATES_PAGE_INTENT,
  UPDATES_SCREEN_LABEL,
  buildUpdatesAskUiContext,
  toSolCategoryKey,
} from "./askUiContext";
export type { UpdatesAskPeriod } from "./askUiContext";

// ─── Summary Builder ───────────────────────────────────────────────────────────

function categoryClause(categoryKey: string, plural: boolean): string {
  const category = getNotificationCategory(categoryKey);
  if (!category) return plural ? "updates" : "update";
  return plural ? category.clausePlural : category.clauseSingular;
}

export function buildNotificationSummary(
  _notifications: Notification[],
  notableItems: Notification[],
  mapped: MappedNotificationCounts,
  unreadTotal: number,
): NotificationSummaryModel {
  const failed = mapped.byStatus.failed;
  const pending = mapped.byStatus["in-progress"];
  const succeeded = mapped.byStatus.completed;

  const clauses: NotificationSummaryClauseModel[] = [
    { id: "total", text: `${mapped.total} update${mapped.total === 1 ? "" : "s"}`, filter: { kind: "all" } },
  ];
  if (succeeded > 0) {
    clauses.push({ id: "succeeded", text: `${succeeded} background operation${succeeded === 1 ? "" : "s"}`, filter: { kind: "status", status: "completed" } });
  }
  if (failed > 0) {
    clauses.push({ id: "failed", text: `${failed} ${failed === 1 ? "operation" : "operations"} failed`, filter: { kind: "status", status: "failed" } });
  }
  if (pending > 0) {
    clauses.push({ id: "pending", text: `${pending} in progress`, filter: { kind: "status", status: "in-progress" } });
  }
  for (const cat of mapped.categories) {
    if (cat.count <= 0) continue;
    clauses.push({
      id: cat.value,
      text: `${cat.count} ${categoryClause(cat.value, cat.count !== 1)}`,
      filter: cat.filter,
    });
  }

  const statusTiles = mapped.statuses
    .filter((s) => s.count > 0 && s.key !== "cancelled")
    .map((s): NotificationSummaryTileModel => ({
      id: s.key,
      eyebrow: s.label,
      value: String(s.count),
      caption:
        s.key === "in-progress" ? "Background operations running"
          : s.key === "completed" ? "Background operations completed"
            : "Failed operations",
      count: s.count,
      classes: s.classes,
      span: s.key === "completed" ? "col-span-4 sm:col-span-4" : "col-span-2 sm:col-span-2",
      filter: s.filter,
      icon: s.icon,
    }));

  const categoryTiles = mapped.categories
    .filter((c) => c.count > 0)
    .map((c): NotificationSummaryTileModel => ({
      id: c.value,
      eyebrow: c.label,
      value: String(c.count),
      caption: getNotificationCategory(c.value)?.caption ?? "",
      count: c.count,
      classes: c.classes,
      span: "col-span-2 sm:col-span-2",
      filter: c.filter,
      icon: c.icon,
    }));

  const categoryProse = clauses
    .filter((clause) => clause.id !== "total" && !["succeeded", "failed", "pending"].includes(clause.id))
    .map((clause) => clause.text)
    .join(", ");

  return {
    total: mapped.total,
    unread: unreadTotal,
    notable: mapped.notable,
    failed,
    pending,
    succeeded,
    clauses,
    statusTiles,
    categoryTiles,
    notableItems,
    askProse:
      mapped.total === 0
        ? "There are no recent updates to ask about."
        : `${mapped.total} updates are available${categoryProse ? `, including ${categoryProse}` : ""}.`,
  };
}
